import { useParams } from 'react-router-dom';
import { useDerivedNetworkContext } from '@/context';
import { useCampaignsQuery, usePayoutCampaignsQuery } from '@/services/revenueApi';
import useFetchQuery from '@/hooks/useFetchQuery';
import type { CampaignsByCategory, PayoutsCampaignsByCategory } from '@/types/api';
import { usePageMetadata } from '@/hooks';

interface UseCampaignTypesCardDataReturn {
  data: CampaignsByCategory | PayoutsCampaignsByCategory;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Custom hook to fetch campaign types data for the Campaign Types Card
 * @returns Object containing campaign types data, network currency, and query status
 */
const useCampaignTypesCardData = (): UseCampaignTypesCardDataReturn => {
  const { year, month } = useParams();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { page: pageType } = usePageMetadata();

  // Earnings queries
  const { queryResult: campaigns } = useFetchQuery(useCampaignsQuery, {
    enabled: pageType === 'earnings',
  });

  // Payouts queries
  const payoutsCampaignsQuery = usePayoutCampaignsQuery(
    selectedNetwork.id ?? '',
    year ?? '',
    month ?? '',
    { enabled: pageType === 'payouts' }
  );

  const data =
    pageType === 'earnings'
      ? (campaigns.data?.campaigns_by_category as CampaignsByCategory)
      : (payoutsCampaignsQuery.data as PayoutsCampaignsByCategory);

  const status = pageType === 'earnings' ? campaigns : payoutsCampaignsQuery;

  return {
    data,
    isPending: status.isPending,
    isError: status.isError,
    error: status.error,
  };
};

export default useCampaignTypesCardData;
